import React, { useState, useEffect } from 'react'
import axios from 'axios';
import { IoFastFoodOutline, IoFastFood, IoPeopleOutline } from "react-icons/io5";
import { FcClock } from "react-icons/fc";
import { RiNumbersLine } from "react-icons/ri";
import KitchenSummarySidebar from './KitchenSummarySidebar';
import KitchenSummaryDetails from './KitchenSummaryDetails';
import { useOutlet } from '../../../../context/OutletContext';
import { KitchenReportPagination } from './KitchenReportPagination';
import PageStatus from '../../../PageStatus/PageStatus';
import KitchenReportTable from './KitchenReportTable';
import KitchenOrderListView from './KitchenOrderListView';
import { convertTime } from '../../../../utils/timeUtils';
import { toast } from 'react-toastify';

const StatCard = ({ icon: Icon, label, value, iconClass = "" }) => (
    <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2 border border-gray-100">
        <div className={`text-xl shrink-0 ${iconClass}`}>
            <Icon />
        </div>
        <div className="min-w-0">
            <div className="text-xs text-gray-500 font-medium truncate">{label}</div>
            <div className="text-sm font-semibold text-gray-800 truncate">{value ?? '—'}</div>
        </div>
    </div>
);

const MODE_DETAIL = "detail";
const MODE_LIST = "list";

const ReportForKitchen = () => {
    const today = new Date().toLocaleDateString("en-CA");

    const [information, setInformation] = useState({});
    const [orders, setOrders] = useState([]);
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [maxPageLength, setMaxPageLength] = useState();
    const [pageLength, setPageLength] = useState([]);
    const [categories, setCategories] = useState({});
    const [categoryVoid, setCategorieVoid] = useState({});
    const [loading, setLoading] = useState(false);
    const [sidebarLoading, setSidebarLoading] = useState(false);
    const [error, setError] = useState("");
    const [sidebarError, setSidebarError] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [mode, setMode] = useState(MODE_DETAIL);

    const url = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");
    const { defaultOutlet } = useOutlet();
    const selectedOutlet = defaultOutlet;

    useEffect(() => {
        setPageLength(Array.from({ length: maxPageLength }, (_, i) => i + 1));
    }, [maxPageLength]);

    const fetchReport = async (page = 1) => {
        if (!token || !selectedOutlet) return;
        try {
            setLoading(true);
            setError("");
            setCurrentPage(page);

            if (!startDate) {
                toast.error("Please select a start date");
                return;
            }

            if (!endDate) {
                toast.error("Please select an end date");
                return;
            }

            if (new Date(startDate) > new Date(endDate)) {
                toast.error("Start date cannot be later than end date");
                return;
            }

            const endpoint = mode === MODE_LIST ? "/get-orders-list" : "/report";
            const response = await axios.post(`${url}${endpoint}`, {
                start_date: startDate,
                end_date: endDate,
                outlet_name: selectedOutlet,
                page_no: page,
                token,
            });

            const data = response.data;

            if (mode === MODE_LIST) {
                setOrders(data.data || []);
                if (page === 1) {
                    setInformation(data.summary || {});
                    setMaxPageLength(data.maxPage_Length);
                }
            } else {
                setOrders(data.orders || []);
                if (page === 1) {
                    setInformation(data);
                    setMaxPageLength(data.maxPage_Length);
                }
            }
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Failed to load report."
            );
            setMaxPageLength(0);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateReport = () => fetchReport(1);
    const handleNumber = (page) => fetchReport(page);

    const handleSidebar = async () => {
        try {
            setSidebarLoading(true);
            setSidebarError("");
            const response = await axios.post(`${url}/itemstats`, {
                start_date: startDate,
                end_date: endDate,
                outlet_name: selectedOutlet,
                token,
            });
            setCategories(response.data.Completed || []);
            setCategorieVoid(response.data.Void || []);
        } catch (err) {
            setSidebarError(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Failed to load item summary."
            );
        } finally {
            setSidebarLoading(false);
        }
    };

    const handleModeSwitch = (newMode) => {
        if (newMode === mode) return;
        setMode(newMode);
        setOrders([]);
        setInformation({});
        setCurrentPage(1);
        setPageLength([]);
    };

    const handleReopenOrder = (tableId) => {
        toast.info(
            <div>
                <p className="mb-3">
                    Move this order back to <strong>Order Received</strong>?
                </p>

                <div className="flex gap-2">
                    <button
                        className="px-3 py-1 rounded bg-[#F3F1EC] hover:bg-[#ECE8E0] text-gray-800"
                        onClick={async () => {
                            try {
                                await axios.post(`${url}/reopen_order`, {
                                    token,
                                    table_id: tableId,
                                });

                                toast.dismiss();

                                toast.success("Order moved back to Order Received.");

                                fetchReport();
                            } catch (err) {
                                toast.dismiss();

                                toast.error(
                                    err?.response?.data?.message ||
                                    err?.response?.data?.error ||
                                    "Failed to reopen order."
                                );
                            }
                        }}
                    >
                        Yes
                    </button>

                    <button
                        className="px-3 py-1 rounded bg-red-500 hover:bg-red-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeOnClick: false,
                draggable: false,
            }
        );
    };

    useEffect(() => {
        if (token && selectedOutlet) fetchReport(1);
    }, [token, selectedOutlet, mode]);

    const { first_orderAt, last_orderAt, Guest_count, Operating_hours, item_count } = information;

    return (
        <div>
            <div className="flex flex-col lg:flex-row">

                {/* ── LEFT SIDEBAR ── */}
                <aside className="w-1/3 bg-white order-b-0 border-r border-gray-200 p-4 sticky top-0 h-screen overflow-y-auto shadow-sm hidden lg:block">
                    <PageStatus loading={loading} error={error} loadingText="Loading summary..." />

                    {!loading && !error && (
                        <>
                            <div className="px-3 py-2.5 border-b border-[#F0EDE8] bg-[#F8F7F3] text-xs font-semibold tracking-[0.15em] uppercase text-[#807e76]">
                                Summary
                            </div>

                            <div className="px-3 py-2.5 overflow-y-auto tiny-scrollbar border-b border-[#F0EDE8]">
                                <KitchenSummaryDetails information={information} />
                            </div>

                            <div className="grid lg:grid-cols-1 xl:grid-cols-2 gap-2 pt-3 px-2.5">
                                <StatCard icon={RiNumbersLine} label="Total Items" value={item_count} iconClass="text-indigo-500" />
                                <StatCard icon={IoFastFoodOutline} label="First Order" value={convertTime(first_orderAt)} iconClass="text-sky-500" />
                                <StatCard icon={IoFastFood} label="Last Order" value={convertTime(last_orderAt)} iconClass="text-orange-500" />
                                <StatCard icon={IoPeopleOutline} label="Guests" value={Guest_count} iconClass="text-violet-500" />
                                <StatCard icon={FcClock} label="Operating Hours" value={Operating_hours} />
                            </div>
                        </>
                    )}
                </aside>

                {/* ── MAIN CONTENT ── */}
                <main className="flex-1 min-w-0 p-3">
                    {/* Filter bar */}
                    <div className="rounded-lg border border-[#E0DDD8] bg-white overflow-hidden mb-3">
                        <div className="p-3 border-b border-[#F0EDE8]">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 xl:flex xl:justify-between xl:items-center text-xs">

                                <div className="flex gap-2 items-center">
                                    <label className="block font-semibold uppercase tracking-wider text-[#807e76]">Start Date</label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="border border-[#E0DDD8] px-2 py-1.5 rounded-md bg-[#FAFAF8] text-[#2C2C2A] font-medium w-[120px] cursor-pointer"
                                    />
                                </div>

                                <div className="flex sm:justify-end gap-3 md:gap-2 items-center">
                                    <label className="block font-semibold uppercase tracking-wider text-[#807e76] pr-0.5 sm:pr-0">End Date</label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="border border-[#E0DDD8] px-2 py-1.5 rounded-md bg-[#FAFAF8] text-[#2C2C2A] font-medium w-[120px] cursor-pointer"
                                    />
                                </div>

                                <button
                                    onClick={handleGenerateReport}
                                    className="px-3 py-2 text-xs rounded-md font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md"
                                >
                                    View Report
                                </button>

                                <KitchenSummarySidebar
                                    handleSidebar={handleSidebar}
                                    categories={categories}
                                    categoryVoid={categoryVoid}
                                    sidebarLoading={sidebarLoading}
                                    sidebarError={sidebarError}
                                />
                            </div>
                        </div>

                        {/* ── Mode toggle ── */}
                        <div className="px-3 py-2 flex items-center gap-1 bg-[#FAFAF8]">
                            <span className="text-xs font-semibold uppercase tracking-wider text-[#807e76] mr-2">View</span>

                            <button
                                onClick={() => handleModeSwitch(MODE_DETAIL)}
                                className={`
                                    flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold
                                    border transition-all duration-150
                                    ${mode === MODE_DETAIL
                                        ? "bg-[#2C2C2A] text-[#F8F7F3] border-[#2C2C2A]"
                                        : "bg-white text-[#5A5854] border-[#E0DDD8] hover:bg-[#F3F1EC]"}
                                `}
                            >
                                {/* grid icon */}
                                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                                    <rect x="0.5" y="0.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
                                    <rect x="7.5" y="0.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
                                    <rect x="0.5" y="7.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
                                    <rect x="7.5" y="7.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
                                </svg>
                                Detailed
                            </button>

                            <button
                                onClick={() => handleModeSwitch(MODE_LIST)}
                                className={`
                                    flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold
                                    border transition-all duration-150
                                    ${mode === MODE_LIST
                                        ? "bg-[#2C2C2A] text-[#F8F7F3] border-[#2C2C2A]"
                                        : "bg-white text-[#5A5854] border-[#E0DDD8] hover:bg-[#F3F1EC]"}
                                `}
                            >
                                {/* list icon */}
                                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                                    <line x1="0.5" y1="2.5" x2="12.5" y2="2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                    <line x1="0.5" y1="6.5" x2="12.5" y2="6.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                    <line x1="0.5" y1="10.5" x2="12.5" y2="10.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                </svg>
                                List
                            </button>
                        </div>
                    </div>

                    {!loading && !error && (
                        <aside className="w-full bg-white border-b border-gray-200 p-3 shadow-sm lg:hidden mb-3">
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-10'>
                                <div>
                                    <div className="p-2.5 border-b border-[#F0EDE8] bg-[#F8F7F3] text-xs font-semibold tracking-[0.15em] uppercase text-[#807e76]">
                                        Summary
                                    </div>

                                    <div className="p-2.5 overflow-y-auto tiny-scrollbar border-b border-[#F0EDE8]">
                                        <KitchenSummaryDetails information={information} />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
                                    <StatCard icon={RiNumbersLine} label="Total Items" value={item_count} iconClass="text-indigo-500" />
                                    <StatCard icon={IoFastFoodOutline} label="First Order" value={convertTime(first_orderAt)} iconClass="text-sky-500" />
                                    <StatCard icon={IoFastFood} label="Last Order" value={convertTime(last_orderAt)} iconClass="text-orange-500" />
                                    <StatCard icon={IoPeopleOutline} label="Guests" value={Guest_count} iconClass="text-violet-500" />
                                    <StatCard icon={FcClock} label="Operating Hours" value={Operating_hours} />
                                </div>
                            </div>
                        </aside>
                    )}

                    <PageStatus loading={loading} error={error} loadingText="Generating report..." />

                    {/* ── View content ── */}
                    {mode === MODE_DETAIL ? (
                        <>
                            <KitchenReportTable
                                loading={loading}
                                error={error}
                                orders={orders}
                                handleReopenOrder={handleReopenOrder}
                            />

                            <KitchenReportPagination
                                pageLength={pageLength}
                                currentPage={currentPage}
                                handleNumber={handleNumber}
                            />
                        </>
                    ) : (
                        <KitchenOrderListView
                            loading={loading}
                            error={error}
                            orders={orders}
                            token={token}
                            selectedOutlet={selectedOutlet}
                            handleReopenOrder={handleReopenOrder}
                        />
                    )}
                </main>
            </div>
        </div>
    );
};

export default ReportForKitchen;